// -----------------------------------------------------------------------------
// HubSidebar
//
// The ~30% right column of the CompletedTreatmentHub split view. Hosts the same
// composers the attended popup uses (IosEmailComposerBody / FollowUpWhatsappChat)
// so the hub's email/WhatsApp checklist actions are completed IN the sidebar —
// reusing the attended sidebar mechanism exactly (see OpenConsultationPopup.jsx).
//
// Tabs:
//   'details'  → HubSidebarDetails (treatment context + light edits)
//   'email'    → IosEmailComposerBody (compact); onSendSuccess → onBack()
//   'whatsapp' → FollowUpWhatsappChat
//
// A back button ("← Treatment details") returns to the details tab. An attention
// pulse ring (keyed by sidebarPulse) replays on each programmatic open to draw
// the eye to the composer — same keyframes pattern as OpenConsultationPopup.
// -----------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import Iconify from 'src/components/Iconify';
import IosEmailComposerBody from 'src/sections/tasks/composers/IosEmailComposerBody';
import FollowUpWhatsappChat from 'src/components/whatsappComponents/FollowUpWhatsappChat';
import HubSidebarDetails from './HubSidebarDetails';

export default function HubSidebar({
    sidebarTab,
    sidebarPulse,
    onBack,
    row,
    leadId,
    practiceId,
    leadDetail,
    followLead,
    handleClose,
    treatmentId,
    treatmentStatus,
}) {
    return (
        <>
            {/* Attention pulse — re-mounts on each programmatic open (keyed by
                sidebarPulse) so the glow ring replays. pointerEvents:none so it
                never blocks interaction. Mirrors OpenConsultationPopup. */}
            {sidebarPulse > 0 && (
                <Box
                    key={sidebarPulse}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 5,
                        pointerEvents: 'none',
                        borderRadius: 'inherit',
                        '@keyframes sidebarAttentionPulse': {
                            '0%': { boxShadow: 'inset 0 0 0 3px rgba(7,141,238,0.55)', opacity: 1 },
                            '60%': { boxShadow: 'inset 0 0 0 3px rgba(7,141,238,0.55)', opacity: 1 },
                            '100%': { boxShadow: 'inset 0 0 0 3px rgba(7,141,238,0)', opacity: 0 },
                        },
                        animation: 'sidebarAttentionPulse 1.6s ease-out forwards',
                    }}
                />
            )}

            {/* Back to details (only when on a composer tab) */}
            {sidebarTab !== 'details' && (
                <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                    <Button
                        data-testid="hub-sidebar-back"
                        size="small"
                        startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={16} height={16} />}
                        onClick={onBack}
                        sx={{
                            color: '#078DEE',
                            textTransform: 'none',
                            fontSize: '13px',
                            fontWeight: 600,
                            px: 0.5,
                            '&:hover': { bgcolor: 'rgba(7,141,238,0.06)' },
                        }}
                    >
                        Treatment details
                    </Button>
                </Box>
            )}

            {sidebarTab === 'details' && (
                <HubSidebarDetails
                    row={row}
                    leadId={leadId}
                    practiceId={practiceId}
                    treatmentId={treatmentId}
                    treatmentStatus={treatmentStatus}
                    handleClose={handleClose}
                />
            )}

            {sidebarTab === 'email' && (
                <Box sx={{ height: '100%' }}>
                    <IosEmailComposerBody
                        leadId={leadId}
                        practiceId={practiceId}
                        leadDetail={leadDetail}
                        compact
                        onSendSuccess={() => onBack()}
                    />
                </Box>
            )}

            {sidebarTab === 'whatsapp' && (
                <Box sx={{ height: '100%' }}>
                    <FollowUpWhatsappChat user={leadDetail} followLead={followLead} />
                </Box>
            )}
        </>
    );
}

HubSidebar.propTypes = {
    sidebarTab: PropTypes.oneOf(['details', 'email', 'whatsapp']),
    sidebarPulse: PropTypes.number,
    onBack: PropTypes.func.isRequired,
    row: PropTypes.object,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    followLead: PropTypes.object,
    handleClose: PropTypes.func,
    treatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    treatmentStatus: PropTypes.string,
};

HubSidebar.defaultProps = {
    sidebarTab: 'details',
    sidebarPulse: 0,
    row: null,
    leadId: null,
    practiceId: null,
    leadDetail: null,
    followLead: null,
    handleClose: null,
    treatmentId: null,
    treatmentStatus: null,
};
